import { useEffect, useState } from 'react';
import { createHttpService } from '../apis/ApiService';

export type TPublicBranding = {
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  instituteName: string | null;
};

/**
 * Extracts the leftmost subdomain from the current hostname and fetches public
 * branding for that institute slug.  Returns `null` while loading and an empty
 * branding object when no matching institute is found.
 *
 * Only runs when `APEX_DOMAIN` subdomain routing is in use (i.e. the hostname
 * has more than two labels).  On a bare domain or localhost it returns null
 * immediately so the default app logo is shown.
 */
export const usePublicBrandingHook = (): TPublicBranding | null => {
  const [branding, setBranding] = useState<TPublicBranding | null>(null);

  useEffect(() => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Need at least subdomain.domain.tld (3 parts) to have a meaningful slug.
    // Also skip "www" and "admin" prefixes.
    if (parts.length < 3) {
      setBranding({ logoUrl: null, primaryColor: null, secondaryColor: null, accentColor: null, instituteName: null });
      return;
    }

    const slug = parts[0];
    if (!slug || slug === 'www' || slug === 'admin') {
      setBranding({ logoUrl: null, primaryColor: null, secondaryColor: null, accentColor: null, instituteName: null });
      return;
    }

    const httpAPI = createHttpService();
    httpAPI.branding
      .get<TPublicBranding>(`/public/by-slug/${encodeURIComponent(slug)}`)
      .then((data) => setBranding(data))
      .catch(() => setBranding({ logoUrl: null, primaryColor: null, secondaryColor: null, accentColor: null, instituteName: null }));
  }, []);

  return branding;
};
